// src/pages/CaseDetailPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { BASE_URL, SCRAPING_BASE_URL } from "../utils";
import LoadingOverlay from "../components/LoadingOverlay";
import { FaPlus, FaMinus } from "react-icons/fa";
import { FiEye, FiPaperclip, FiCopy } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

export default function CaseDetailPage() {
  const { caseId, court, petition } = useParams();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  // notes state
  const [statusNotes, setStatusNotes] = useState({});
  const [showFullLastNote, setShowFullLastNote] = useState(false);
  // contentEditable editor ref
  const editorRef = useRef(null);
  // applied raw HTML (pasted and preserved)
  const [appliedRawHtml, setAppliedRawHtml] = useState("");
  // paste modal text area
  const [pasteHtmlText, setPasteHtmlText] = useState("");
  const [showPasteHtml, setShowPasteHtml] = useState(false);

  // HTML viewer modal for last note
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  // currently loaded note id (when user loads/edit existing note)
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [currentNoteTitle, setCurrentNoteTitle] = useState("");

  const [activeStatus, setActiveStatus] = useState("Approved");
  const [subStatus, setSubStatus] = useState("NA");
  const [followUpDate, setFollowUpDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [label, setLabel] = useState("");
  const [followUpRemark, setFollowUpRemark] = useState("");

  // edit mode fields
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  // other controls
  const [refreshPopup, setRefreshPopup] = useState(false);
  const [refreshDate, setRefreshDate] = useState("");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showProperties, setShowProperties] = useState(false);

  // PDF upload states (for main editor)
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // small helpers
  const subStatusOptions = [
    "NA",
    "Research",
    "Follow Up",
    "Not Selling",
    "Evernote",
    "Selling",
    "fresh lead",
  ];
  const followUpRemarkOptions = [
    "Selling",
    "Not Selling",
    "Undecided",
    "Left Voicemail",
    "NA",
    "Call Back Requested",
    "Meeting Scheduled",
    "Important in follow up",
  ];
  const formatDate = (value) =>
    value ? dayjs(value).format("MMM D, YYYY") : "—";
  const formatDateTime = (value) =>
    value ? dayjs(value).format("MMM D, YYYY, h:mm A") : "—";

  // ---------- fetch ----------
  const extractAddress = (desc) => {
    if (!desc) return "";
    const s = String(desc).trim();
    const i = s.toLowerCase().indexOf(" located at ");
    if (i !== -1) return s.slice(i + " located at ".length).trim();
    return s.replace(/^a\s*\(.+?\)\s*.*?\s+house\s+/i, "").trim();
  };

  const fetchCaseData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/case/${caseId}/${court}/${petition}`,
      );
      if (res.data) {
        const data = res.data;
        const addressOnly = extractAddress(
          data.decedent_real_property_description,
        );
        setCaseData({
          ...data,
          decedent_real_property_address_only: addressOnly,
          secondary_phone: data.secondary_phone || "",
          secondary_email: data.secondary_email || "",
        });
        setStatusNotes(res.data.notes || {});
        setActiveStatus(res.data.status || "Approved");
        setSubStatus(res.data.sub_status || "NA");
        setLabel(res.data.label || "");
        setFollowUpRemark(res.data.remark || "");

        // initialize editor with server-provided "current_note_text" if present
        const initialHtml = res.data.current_note_text || "" || "";
        if (editorRef.current) editorRef.current.innerHTML = initialHtml;
      }
    } catch (err) {
      console.error("Error fetching case data:", err);
      toast.error("Failed to load case.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseData();
  }, [caseId, court, petition]);

  // ---------- helpers ----------
  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/\u00A0/g, " ")
      .trim();
  };

  const escapeHtml = (str) =>
    String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const renderNoteContent = (text) => {
    if (!text) return { __html: "—" };
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
    if (looksLikeHtml) return { __html: text };
    return { __html: escapeHtml(text).replace(/\n/g, "<br/>") };
  };

  // NEW helpers for Last Note modal/preview
  const sanitizeHtmlForView = (html) => {
    if (!html) return "";
    // remove <script> and <style> blocks and inline event handlers (basic sanitization)
    let s = html.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
    s = s.replace(/\son\w+="[^"]*"/gi, "");
    s = s.replace(/\son\w+='[^']*'/gi, "");
    // Use DOMPurify to be safer
    try {
      s = DOMPurify.sanitize(s, {
        ADD_ATTR: ["target", "class", "style", "data-*"],
      });
    } catch (e) {
      /* ignore */
    }
    return s;
  };

  const isComplexHtml = (html) => {
    if (!html) return false;
    const lowered = html.toLowerCase();
    const tagCount = (html.match(/<\w+/g) || []).length;
    if (tagCount > 10) return true;
    if (lowered.includes("startfragment")) return true;
    if (
      lowered.includes("<div") ||
      lowered.includes("<table") ||
      lowered.includes("<img")
    )
      return true;
    if (html.length > 1000) return true;
    return false;
  };

  const firstNWords = (text, n) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= n) return words.join(" ");
    return words.slice(0, n).join(" ");
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(html || "");
    setHtmlModalOpen(true);
  };

  // build pdf href helper (prefer absolute, else BASE_URL fallback)
  const buildPdfHref = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://"))
      return pdf_url;
    // ensure leading slash
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  // ---------- field change helper (used by editable fields) ----------
  const handleChange = (field, value) => {
    setCaseData((p) => ({ ...(p || {}), [field]: value }));
    setDirty((d) => ({ ...(d || {}), [field]: true }));
  };

  // ---------- queue toggle ----------
  const handleQueueUpdate = async (newQuied) => {
    const isAdding = newQuied === 1;
    const confirmMsg = isAdding
      ? "Add this case to the Friday check queue?"
      : "Remove this case from the Friday check queue?";
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      await axios.put(`${BASE_URL}/api/case/${caseId}`, {
        caseId,
        quied: newQuied,
        petition_court_type: caseData.petition_court_type,
        petition_processing_file_type: caseData.petition_processing_file_type,
      });
      toast.success(
        isAdding ? "Case added to queue!" : "Case removed from queue!",
      );
      await fetchCaseData();
    } catch (err) {
      console.error("Failed to update queue:", err);
      toast.error("Failed to update queue.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Paste raw HTML → preserve ----------
  const applyPastedHtml = () => {
    try {
      if (!pasteHtmlText || !pasteHtmlText.trim()) {
        toast.error("No HTML provided.");
        return;
      }

      // sanitize but keep style/class/src/href etc so visual fidelity is kept
      const sanitized = DOMPurify.sanitize(pasteHtmlText, {
        ALLOWED_ATTR: [
          "style",
          "class",
          "src",
          "href",
          "alt",
          "title",
          "target",
          "data-*",
        ],
      });

      if (!sanitized || sanitized.trim() === "") {
        toast.error("Sanitization removed all content — paste different HTML.");
        return;
      }

      // Preserve sanitized HTML for preview and default saving.
      setAppliedRawHtml(sanitized);

      // Put into editor so user sees it (but saving will prefer appliedRawHtml)
      if (editorRef.current) editorRef.current.innerHTML = sanitized;

      setShowPasteHtml(false);
      setPasteHtmlText("");
      setCurrentNoteId(null);
      setCurrentNoteTitle("");
      toast.success(
        "Raw HTML applied and preserved for preview. Click Save to persist.",
      );
    } catch (err) {
      console.error("applyPastedHtml error:", err);
      toast.error("Failed to apply pasted HTML.");
    }
  };

  // ---------- Notes CRUD helper functions (uses your API patterns) ----------
  const updateNoteViaCaseEdit = async (noteId, newHtml) => {
    return axios.put(`${BASE_URL}/api/case/${caseId}/edit-note`, {
      noteId,
      caseId,
      newText: newHtml,
    });
  };

  const deleteNoteById = async (id) => {
    try {
      return await axios.delete(`${BASE_URL}/api/note/${id}`);
    } catch (err) {
      try {
        return await axios.delete(`${BASE_URL}/api/case/${caseId}/note/${id}`);
      } catch (e) {
        throw err;
      }
    }
  };

  // ---------- PDF upload for main editor (attach to new note on save by sending pdf_url) ----------
  const handlePdfUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Please upload a PDF file.");
    }

    const formData = new FormData();
    // backend expects field name 'pdf' per your server code
    formData.append("pdf", file);

    try {
      setUploadingPdf(true);
      const uploadRes = await axios.post(`${BASE_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl =
        uploadRes?.data?.url ||
        uploadRes?.data?.fileURL ||
        uploadRes?.data?.fileUrl ||
        uploadRes?.data?.url;
      if (!uploadedUrl) {
        throw new Error("Upload returned no URL.");
      }
      setUploadedPdfUrl(uploadedUrl);
      toast.success(
        "📄 PDF uploaded successfully and ready to attach to note.",
      );
    } catch (err) {
      console.error("PDF upload error:", err);
      toast.error("Failed to upload PDF.");
    } finally {
      setUploadingPdf(false);
    }
  };

  // ---------- Save status (single Save button) ----------
  const handleSaveStatus = async () => {
    // Build HTML - prefer appliedRawHtml for fidelity
    const editorHtml = editorRef.current ? editorRef.current.innerHTML : "";
    const htmlToSave =
      appliedRawHtml && appliedRawHtml.trim()
        ? appliedRawHtml.trim()
        : (editorHtml || "").trim();
    const plain = stripHtml(htmlToSave);

    // Basic validation: require note content when status != Rejected (or an uploaded PDF)
    if (activeStatus !== "Rejected" && !plain.trim() && !uploadedPdfUrl) {
      return alert(
        "⚠️ Please enter a note or attach a PDF before saving status.",
      );
    }

    // require subStatus & label when Approved (per your request)
    if (activeStatus === "Approved") {
      if (!subStatus || subStatus === "") {
        return alert(
          "⚠️ Please choose a valid Sub-Status before saving (cannot be NA).",
        );
      }
      if (!label || label === "") {
        return alert("⚠️ Please select a Label before saving.");
      }
    }

    // Additional check for Follow Up date format if subStatus === Follow Up
    if (subStatus === "Follow Up") {
      if (!followUpRemark || followUpRemark.trim() === "") {
        return alert("Please select a Remark for Follow Up.");
      }
      // require a MM/DD/YYYY in the plain text OR followUpDate state
      const match = plain.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/);
      if (!match && !followUpDate) {
        return alert(
          "⚠️ Please include a follow-up date in MM/DD/YYYY format in the note or pick the follow-up calendar.",
        );
      }
    }

    setSaveLoading(true);
    try {
      // If editing existing note, update it first (edit-note), and update case status WITHOUT creating a new note
      if (currentNoteId) {
        try {
          // update text
          await updateNoteViaCaseEdit(currentNoteId, htmlToSave);
          toast.success("Note updated.");

          // attach pdf to existing note if present
          if (uploadedPdfUrl) {
            try {
              await axios.put(
                `${BASE_URL}/api/case/${caseId}/note/${currentNoteId}/attach-pdf`,
                { pdf_url: uploadedPdfUrl },
              );
              toast.success("PDF attached to existing note.");
            } catch (attachErr) {
              console.warn("Failed to attach PDF to existing note:", attachErr);
            }
          }

          // after updating note, update case status but don't pass note content (avoids duplicate)
          const payloadNoNote = {
            status: activeStatus,
            note: null,
            plain_text: stripHtml(htmlToSave),
            subStatus: activeStatus === "Approved" ? subStatus : null,
            label: activeStatus === "Approved" ? label : null,
            remark:
              activeStatus === "Approved" && subStatus === "Follow Up"
                ? followUpRemark
                : null,
            petition_type: caseData?.petition_processing_file_type || null,
            petition_court: caseData?.petition_court_type || null,
          };
          await axios.put(
            `${BASE_URL}/api/case/${caseId}/status`,
            payloadNoNote,
          );
          toast.success("Case status updated.");
        } catch (err) {
          console.warn(
            "Failed to update existing note via edit-note (continuing to try status update):",
            err,
          );
        }
      } else {
        // Creating a new note via the status endpoint (existing behavior) — include pdf_url if uploaded
        const payload = {
          status: activeStatus,
          note: htmlToSave,
          plain_text: plain,
          subStatus: activeStatus === "Approved" ? subStatus : null,
          label: activeStatus === "Approved" ? label : null,
          remark:
            activeStatus === "Approved" && subStatus === "Follow Up"
              ? followUpRemark
              : null,
          petition_type: caseData?.petition_processing_file_type || null,
          petition_court: caseData?.petition_court_type || null,
          pdf_url: uploadedPdfUrl || null,
        };
        const res = await axios.put(
          `${BASE_URL}/api/case/${caseId}/status`,
          payload,
        );
        toast.success(res?.data?.message || "✅ Status saved successfully.");
        // update local list quickly
        const now = new Date().toLocaleString();
        const updatedNotes = {
          ...statusNotes,
          [activeStatus]: [
            ...(statusNotes[activeStatus] || []),
            {
              text: htmlToSave,
              timestamp: now,
              pdf_url: uploadedPdfUrl || null,
            },
          ],
        };
        setStatusNotes(updatedNotes);
      }

      // Reset editor/raw and editing state
      setTimeout(() => {
        // refresh to reflect server-state (keeps behavior similar to previous code)
        window.location.reload();
      }, 600);
      setAppliedRawHtml("");
      if (editorRef.current) editorRef.current.innerHTML = "";
      setCurrentNoteId(null);
      setCurrentNoteTitle("");
      setUploadedPdfUrl(null);
    } catch (err) {
      console.error("Error saving status:", err);
      toast.error("❌ Failed to save status.");
    } finally {
      setSaveLoading(false);
    }
  };

  // ---------- Load note into editor ----------
  const loadNoteIntoEditorForEdit = (note) => {
    if (!note) return;
    setCurrentNoteId(note.noteId || note.id || null);
    setCurrentNoteTitle(note.title || "");
    setAppliedRawHtml("");
    if (editorRef.current)
      editorRef.current.innerHTML =
        note.text || note.html || note.text_html || "";
    // if note has attached pdf, show it in upload preview
    setUploadedPdfUrl(note.pdf_url || note.pdfUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Loaded note into editor for editing.");
  };

  // ---------- Delete note ----------
  const handleDeleteNote = async (note) => {
    const id = note.noteId || note.id;
    if (!id) return alert("Cannot delete: no id.");
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteNoteById(id);
      toast.success("Note deleted.");
      await fetchCaseData();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete note.");
    }
  };

  // ---------- paste clipboard handler ----------
  const handleEditorPaste = (e) => {
    if (!e.clipboardData) return;
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) insertHtmlAtCaret(html);
    else if (text) insertHtmlAtCaret(text.replace(/\r\n|\r|\n/g, "<br>"));
  };

  function insertHtmlAtCaret(html) {
    try {
      document.execCommand("insertHTML", false, html);
      return;
    } catch {}
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      if (editorRef.current)
        editorRef.current.insertAdjacentHTML("beforeend", html);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement("div");
    el.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node;
    while ((node = el.firstChild)) frag.appendChild(node);
    range.insertNode(frag);
    if (frag.lastChild) {
      range.setStartAfter(frag.lastChild);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  // ---------- UI: onFollowUp pick -> insert follow-up at START of content ----------
  const handleFollowUpPick = (date) => {
    if (!date) return;
    const formattedDisplay = date.toLocaleDateString("en-US");
    const isoDate = date.toISOString().split("T")[0];
    setFollowUpDate(isoDate);
    // Build follow-up HTML block to insert at start
    const followHtml = `<p><strong>Follow-up on: ${formattedDisplay}</strong></p>`;
    // get current editor/html content (prefer appliedRawHtml if present)
    const currentHtml =
      appliedRawHtml && appliedRawHtml.trim()
        ? appliedRawHtml
        : (editorRef.current ? editorRef.current.innerHTML : "") || "";
    const combined = followHtml + (currentHtml || "");
    // set into editor and clear appliedRawHtml (editor is authoritative now)
    if (editorRef.current) editorRef.current.innerHTML = combined;
    setAppliedRawHtml(""); // editing now authoritative
    setShowCalendar(false);
  };

  if (loading) return <LoadingOverlay />;
  if (!caseData)
    return <div className="p-10 text-red-600">⚠️ No case data available.</div>;

  const filenameOnly = caseData.pdf_filename?.split("\\").pop() || "";
  const documentsHref = filenameOnly
    ? `${BASE_URL}/static/${filenameOnly}`
    : "";
  const hasAdministrator = (caseData.propertyDetails || []).some(
    (p) => p.role?.toUpperCase() === "ADMINISTRATOR",
  );

  // inside your component (above return)
  async function copyToClipboard(
    text,
    successMsg = "Copied",
    failMsg = "Copy failed",
  ) {
    const payload = text == null ? "" : String(text);

    // try modern clipboard API first (works on https or localhost)
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(successMsg);
        return true;
      } catch (err) {
        console.warn(
          "navigator.clipboard.writeText failed, falling back:",
          err,
        );
        // fall through to textarea fallback
      }
    }

    // fallback: textarea + execCommand
    try {
      const textarea = document.createElement("textarea");
      textarea.value = payload;

      // avoid scrolling to bottom on mobile, make invisible
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.width = "1px";
      textarea.style.height = "1px";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length); // for mobile

      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (ok) {
        toast.success(successMsg);
        return true;
      } else {
        console.error("execCommand('copy') returned false");
        toast.error(failMsg);
        return false;
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(failMsg);
      return false;
    }
  }

  // decode HTML entities like &nbsp;, &amp;, etc.
  function decodeHtmlEntities(str) {
    if (!str) return "";
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = String(str);
      return txt.value;
    } catch (err) {
      return String(str);
    }
  }

  // safe getter for first note's pdf url (handles Approved vs approved keys)
  function getFirstNotePdfUrl(caseData) {
    if (!caseData || !caseData.notes) return null;
    const notes = caseData.notes;
    const first =
      (notes.Approved && notes.Approved[0]) ||
      (notes.approved && notes.approved[0]) ||
      (Array.isArray(notes) && notes[0]) ||
      null;
    if (!first) return null;
    return first.pdf_url || first.pdfUrl || first.url || null;
  }

  return (
    <>
      {/* safety CSS */}
      <style>{`
        html, body, #root { overflow-x: hidden; }
        .p-6 { overflow-x: hidden; }
        .raw-html-preview { box-sizing: border-box; max-width: 100%; overflow-x: auto; overflow-y: auto; -webkit-overflow-scrolling: touch; word-wrap: break-word; word-break: break-word; }
        .raw-html-preview * { box-sizing: border-box; max-width: 100% !important; height: auto !important; word-break: break-word !important; }
        .raw-html-preview img, .raw-html-preview video, .raw-html-preview iframe, .raw-html-preview svg { max-width: 100% !important; height: auto !important; display: block; }
        .raw-html-preview table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse; word-break: break-word !important; }
        .raw-html-preview td, .raw-html-preview th { word-break: break-word !important; white-space: normal !important; }
        .raw-html-preview pre { white-space: pre-wrap !important; word-break: break-word !important; max-width: 100% !important; overflow-x: auto; }
        [contenteditable] { max-width: 100%; overflow-wrap: break-word; word-break: break-word; }
      `}</style>

      {/* IMPORTANT: make this container scrollable inside the viewport to avoid bottom cutoff */}
      <div
        className="p-6 bg-white rounded-lg shadow relative"
        style={{
          // keep some space for fixed header/navigation outside this component
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          paddingBottom: 48,
        }}
      >
        {(refreshLoading || loading) && <LoadingOverlay />}

        {/* Header row */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-bold flex items-center gap-3">
            Case Details
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${hasAdministrator ? "bg-green-100 text-green-700 border border-green-300" : "bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}
            >
              {caseId}
            </span>
            <div className="flex items-center gap-2">
              {caseData.quied === 1 ? (
                <>
                  <span
                    id="tooltip-added"
                    className="bg-green-100 text-green-500 text-xs px-2 py-0.5 shadow cursor-default"
                  >
                    Added
                  </span>
                  <Tooltip
                    anchorSelect="#tooltip-added"
                    place="bottom"
                    content="This case ID is added in queue for latest update check every Friday"
                    className="custom-tooltip"
                  />
                  <button
                    id="tooltip-remove"
                    onClick={() => handleQueueUpdate(0)}
                    className="bg-red-500 text-white text-xs px-2 py-0.5 rounded shadow hover:bg-red-600 transition"
                  >
                    Remove
                  </button>
                </>
              ) : (
                <button
                  id="tooltip-add"
                  onClick={() => handleQueueUpdate(1)}
                  className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded shadow hover:bg-blue-600 transition"
                >
                  Add
                </button>
              )}
            </div>
          </h1>

          <div className="flex items-center gap-2">
            {!editMode ? (
              <button
                onClick={() => {
                  if (!window.confirm("Enable editing for this case?")) return;
                  setEditMode(true);
                  setDirty({});
                  toast.info(
                    "Editing enabled. Make your changes and click Save.",
                  );
                }}
                className="px-2 py-1 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600"
              >
                ✏️ Edit fields
              </button>
            ) : (
              <>
                <button
                  onClick={async () => {
                    if (!caseData?.id) {
                      toast.error("Missing row id to save fields.");
                      return;
                    }
                    setSaving(true);
                    try {
                      const editableMap = {
                        petitioner_name: caseData.petitioner_name,
                        petitioner_domicile_address:
                          caseData.petitioner_domicile_address,
                        petitioner_city: caseData.petitioner_city,
                        petitioner_county: caseData.petitioner_county,
                        petitioner_state: caseData.petitioner_state,
                        petitioner_zip: caseData.petitioner_zip,
                        petitioner_citizenship: caseData.petitioner_citizenship,
                        petitioner_interest_of_petitioner:
                          caseData.petitioner_interest_of_petitioner,
                        petitioner_telephone_number:
                          caseData.petitioner_telephone_number,
                        email: caseData.email,
                        decedent_name: caseData.decedent_name,
                        decedent_domicile_address:
                          caseData.decedent_domicile_address,
                        decedent_city: caseData.decedent_city,
                        decedent_county: caseData.decedent_county,
                        decedent_state: caseData.decedent_state,
                        decedent_zip: caseData.decedent_zip,
                        decedent_citizenship: caseData.decedent_citizenship,
                        decedent_place_of_death:
                          caseData.decedent_place_of_death,
                        decedent_personal_property_value:
                          caseData.decedent_personal_property_value,
                        decedent_real_property_value:
                          caseData.decedent_real_property_value,
                        decedent_real_property_description:
                          caseData.decedent_real_property_address_only,
                        secondary_phone: caseData.secondary_phone,
                        secondary_email: caseData.secondary_email,
                      };

                      let updates = Object.entries(dirty)
                        .map(([key]) => {
                          if (key === "decedent_real_property_address_only") {
                            return {
                              field: "decedent_real_property_description",
                              value:
                                caseData.decedent_real_property_address_only ??
                                "",
                            };
                          }
                          if (!(key in editableMap)) return null;
                          return { field: key, value: caseData[key] ?? "" };
                        })
                        .filter(Boolean);

                      if (updates.length === 0) {
                        toast.info("No changes to save.");
                        setEditMode(false);
                        setDirty({});
                        return;
                      }

                      const res = await axios.put(
                        `${BASE_URL}/api/case/${caseData.id}/fields`,
                        {
                          updates,
                          petition_court_type: caseData.petition_court_type,
                          petition_processing_file_type:
                            caseData.petition_processing_file_type,
                        },
                      );

                      const updatedFields = (
                        res?.data?.updated_fields || []
                      ).map((f) =>
                        f
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase()),
                      );

                      if (updatedFields.length > 0) {
                        toast.success(`✅ Saved: ${updatedFields.join(", ")}`);
                      } else {
                        toast.success(
                          res?.data?.message || "✅ Changes saved.",
                        );
                      }

                      setEditMode(false);
                      setDirty({});
                      await fetchCaseData();
                    } catch (err) {
                      console.error(err);
                      const msg =
                        err?.response?.data?.error ||
                        "Some fields failed to save. Please try again.";
                      toast.error(`❌ ${msg}`);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="px-2 py-1 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                >
                  {saving ? "Saving…" : "💾 Save Changes"}
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setDirty({});
                    fetchCaseData();
                  }}
                  className="px-2 py-1 rounded-md text-sm font-medium bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--surface-muted)] "
                >
                  Cancel
                </button>
              </>
            )}

            <button
              onClick={() => setRefreshPopup(true)}
              disabled={refreshLoading}
              className={`flex items-center gap-2 px-2 py-1 rounded-md text-sm font-medium transition-all duration-200 ${refreshLoading ? "bg-gray-400 text-white cursor-not-allowed" : "bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white hover:shadow-lg"}`}
            >
              {refreshLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* ... keep your other panels exactly as before (real property, meta, petitioner/decedent) ... */}

        {/* Real property etc (unchanged) */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div className="p-4 border rounded-lg bg-gray-50">
            <div className="mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                🏠 Real Property
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="w-full">
                <label className="block text-sm text-gray-700 mb-1">
                  Decedent Real Property Address
                </label>
                <div className="text-sm text-gray-800 ">
                  <Field
                    textarea
                    value={caseData.decedent_real_property_address_only || ""}
                    editable={editMode}
                    onChange={(v) =>
                      handleChange("decedent_real_property_address_only", v)
                    }
                    className="m-0 w-full"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Only the address; phrasing like 'A (2) two family house
                  located at' is ignored.
                </p>
              </div>

              <div className="w-full flex md:justify-center items-start">
                <div className="max-w-[220px] w-full">
                  <label className="block text-sm text-gray-700 mb-1 text-right md:text-left md:pl-0">
                    Decedent Real Property Value
                  </label>
                  <div className="text-sm text-gray-800  text-right">
                    <Field
                      value={
                        caseData.decedent_real_property_value != null
                          ? String(caseData.decedent_real_property_value)
                          : ""
                      }
                      editable={editMode}
                      onChange={(v) =>
                        handleChange("decedent_real_property_value", v)
                      }
                      className="m-0 w-full text-right"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full flex items-start md:items-center md:justify-end">
                <div className="flex items-center gap-3">
                  <strong className="text-sm">Properties:</strong>

                  <button
                    onClick={() => setShowProperties(!showProperties)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm border-2 text-white ${showProperties ? "bg-red-600" : hasAdministrator ? "bg-green-600" : "bg-blue-600"}`}
                    title={showProperties ? "Collapse" : "Expand"}
                    style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}
                  >
                    {showProperties ? (
                      <FaMinus size={12} />
                    ) : (
                      <FaPlus size={12} />
                    )}
                  </button>

                  <span className="text-xs text-gray-600">View</span>
                </div>
              </div>
            </div>

            {showProperties && (
              <div className="overflow-x-auto border rounded mt-4">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-gray-100 text-gray-700 uppercase">
                    <tr>
                      <th className="px-3 py-2 border font-semibold">
                        Party Name
                      </th>
                      <th className="px-3 py-2 border font-semibold">Role</th>
                      <th className="px-3 py-2 border font-semibold">DOD</th>
                      <th className="px-3 py-2 border font-semibold">
                        Appointed
                      </th>
                      <th className="px-3 py-2 border font-semibold">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caseData.propertyDetails || []).map((party, idx) => (
                      <tr
                        key={idx}
                        className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100 transition`}
                      >
                        <td className="px-3 py-2 border">
                          {party.party_name || "—"}
                        </td>
                        <td className="px-3 py-2 border">
                          {String(party.role || "—").toUpperCase() ===
                          "ADMINISTRATOR" ? (
                            <span className="inline-block confirm-pill--confirmed px-2 py-0.5 rounded-full text-[11px] font-bold">
                              ADMINISTRATOR
                            </span>
                          ) : (
                            party.role || "—"
                          )}
                        </td>
                        <td className="px-3 py-2 border">{party.dod || "—"}</td>
                        <td className="px-3 py-2 border">
                          {party.appointed || "—"}
                        </td>
                        <td className="px-3 py-2 border">
                          {party.active || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Meta panel (unchanged except Last Note replaced) */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-3 grid md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
            <MetaRow
              label="Petition Processing File Type"
              value={caseData.petition_processing_file_type}
            />
            <MetaRow
              label="Petition Date"
              value={formatDate(caseData.petition_date)}
            />
            <MetaRow
              label="Petition Court Type"
              value={caseData.petition_court_type}
            />

            <MetaRow
              label="Updated Date"
              value={formatDateTime(caseData.updated_date)}
            />

            {/* Last Note with 20-word rule & complex-HTML view */}
            <div className="flex flex-col">
              <div className="text-[12px] text-gray-500">Last Note</div>
              <div className="text-sm text-gray-800 break-words">
                {(() => {
                  const raw = caseData?.last_note || "";
                  // strip HTML tags first (your stripHtml), then decode entities like &nbsp;
                  const plain = decodeHtmlEntities(stripHtml(raw)).trim();
                  if (!plain) return "—";

                  const complex = isComplexHtml(raw);

                  // safe pdf detection for first note
                  const pdfHref = buildPdfHref(getFirstNotePdfUrl(caseData));

                  if (complex) {
                    const preview = firstNWords(plain, 5);
                    return (
                      <div>
                        <div className="text-[13px] text-gray-800 mb-2">
                          {preview}
                          {plain.split(/\s+/).filter(Boolean).length > 20
                            ? "..."
                            : ""}
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => showHtmlContent(raw)}
                            className="p-1 rounded hover:bg-gray-100 inline-flex items-center"
                            title="View full HTML"
                          >
                            <FiEye size={16} className="text-blue-600" />
                          </button>

                          {pdfHref && (
                            <a
                              href={pdfHref}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Open attached PDF"
                              className="p-1 rounded hover:bg-gray-100 inline-flex items-center"
                            >
                              <FiPaperclip
                                size={16}
                                className="text-gray-700"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // simple text -> 20 words inline with view more toggle
                  const words = plain.split(/\s+/).filter(Boolean);
                  const isLong = words.length > 5;
                  const preview = isLong
                    ? words.slice(0, 5).join(" ") + "..."
                    : words.join(" ");

                  if (!isLong) {
                    return (
                      <div style={{ maxWidth: "100%" }}>
                        <div className="text-[13px] text-gray-800">
                          {preview}
                        </div>
                        {pdfHref && (
                          <a
                            href={pdfHref}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 underline"
                          >
                            <FiPaperclip size={14} /> View PDF
                          </a>
                        )}
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="text-[13px] text-gray-800 mb-1">
                        {showFullLastNote ? plain : preview}
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setShowFullLastNote((s) => !s)}
                          className="ml-0 text-xs text-indigo-600 underline"
                          aria-expanded={showFullLastNote}
                        >
                          {showFullLastNote ? "View less" : "View more"}
                        </button>

                        {pdfHref && (
                          <a
                            href={pdfHref}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 underline"
                          >
                            <FiPaperclip size={14} /> View PDF
                          </a>
                        )}

                        <button
                          onClick={() => {
                            try {
                              navigator.clipboard?.writeText(plain);
                              toast.info("Copied plain text to clipboard");
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          className="p-1 rounded hover:bg-gray-100 inline-flex items-center"
                          title="Copy plain text"
                        >
                          <FiCopy size={14} className="text-gray-700" />
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <MetaRow
              label="Latest Date"
              value={formatDateTime(caseData.latest_date)}
            />
            <MetaRow label="Sub Status" value={caseData.sub_status || "—"} />
            <MetaRow label="Label" value={caseData.label || "—"} />
            <MetaRow
              label="Documents"
              value={
                documentsHref ? (
                  <a
                    href={documentsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    {filenameOnly}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <MetaRow
              label="Created At"
              value={formatDateTime(caseData.created_at)}
            />
            <MetaRow
              label="Updated At"
              value={formatDateTime(caseData.updated_at)}
            />
            <MetaRow
              label="Remakr"
              value={caseData.remark || "NA"}
            />
          </div>
        </div>

        {/* === Primary grid: Decedent | Petitioner (now two-column full width) === */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Petitioner */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold text-lg mb-3">👤 Petitioner</h3>

            <Field
              label="Petitioner Name"
              value={caseData.petitioner_name || ""}
              editable={editMode}
              onChange={(v) => handleChange("petitioner_name", v)}
            />
            <Field
              label="Petitioner Domicile Address"
              textarea
              value={caseData.petitioner_domicile_address || ""}
              editable={editMode}
              onChange={(v) => handleChange("petitioner_domicile_address", v)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Petitioner City"
                value={caseData.petitioner_city || ""}
                editable={editMode}
                onChange={(v) => handleChange("petitioner_city", v)}
              />
              <Field
                label="Petitioner County"
                value={caseData.petitioner_county || ""}
                editable={editMode}
                onChange={(v) => handleChange("petitioner_county", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Petitioner State"
                value={caseData.petitioner_state || ""}
                editable={editMode}
                onChange={(v) => handleChange("petitioner_state", v)}
              />
              <Field
                label="Petitioner Zip"
                value={caseData.petitioner_zip || ""}
                editable={editMode}
                onChange={(v) => handleChange("petitioner_zip", v)}
              />
            </div>
            <Field
              label="Petitioner Interest Of Petitioner"
              textarea
              value={caseData.petitioner_interest_of_petitioner || ""}
              editable={editMode}
              onChange={(v) =>
                handleChange("petitioner_interest_of_petitioner", v)
              }
            />
            <Field
              label="Petitioner Citizenship"
              value={caseData.petitioner_citizenship || ""}
              editable={false}
            />

            {/* Phones/Emails */}
            <Field
              label="Phone No"
              value={caseData.petitioner_telephone_number || ""}
              editable={editMode}
              onChange={(v) => handleChange("petitioner_telephone_number", v)}
            />
            <Field
              label="Email"
              value={caseData.email || ""} // if your schema has petitioner_email, rename
              editable={editMode}
              onChange={(v) => handleChange("email", v)}
            />
            <Field
              label="Secondary Phone No"
              value={caseData.secondary_phone || ""}
              editable={editMode}
              onChange={(v) => handleChange("secondary_phone", v)}
            />
            <Field
              label="Secondary Email"
              value={caseData.secondary_email || ""}
              editable={editMode}
              onChange={(v) => handleChange("secondary_email", v)}
            />
          </div>
          {/* Decedent */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold text-lg mb-3">🧾 Decedent</h3>

            <Field
              label="Decedent Name"
              value={caseData.decedent_name || ""}
              editable={editMode}
              onChange={(v) => handleChange("decedent_name", v)}
            />
            <Field
              label="Decedent Date Of Death"
              value={formatDate(caseData.decedent_date_of_death)}
              editable={false}
            />
            <Field
              label="Decedent Domicile Address"
              textarea
              value={caseData.decedent_domicile_address || ""}
              editable={editMode}
              onChange={(v) => handleChange("decedent_domicile_address", v)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Decedent City"
                value={caseData.decedent_city || ""}
                editable={editMode}
                onChange={(v) => handleChange("decedent_city", v)}
              />
              <Field
                label="Decedent County"
                value={caseData.decedent_county || ""}
                editable={editMode}
                onChange={(v) => handleChange("decedent_county", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Decedent State"
                value={caseData.decedent_state || ""}
                editable={editMode}
                onChange={(v) => handleChange("decedent_state", v)}
              />
              <Field
                label="Decedent Zip"
                value={caseData.decedent_zip || ""}
                editable={editMode}
                onChange={(v) => handleChange("decedent_zip", v)}
              />
            </div>

            <Field
              label="Decedent Citizenship"
              value={caseData.decedent_citizenship || ""}
              editable={false}
            />
            <Field
              label="Decedent Place Of Death"
              value={caseData.decedent_place_of_death || ""}
              editable={false}
            />
            <Field
              label="Decedent Personal Property Value"
              value={
                caseData.decedent_personal_property_value != null
                  ? String(caseData.decedent_personal_property_value)
                  : ""
              }
              editable={false}
            />
          </div>
        </div>

        {/* NOTES panel (unchanged) */}
        <div className="mb-6">
          <style>{`
            .raw-html-preview { box-sizing: border-box; max-width: 100%; overflow-x: auto; overflow-y: auto; word-wrap: break-word; }
            .raw-html-preview * { box-sizing: border-box; max-width: 100% !important; }
            .raw-html-preview img, .raw-html-preview video, .raw-html-preview iframe { max-width: 100% !important; height: auto !important; display: block; }
            .raw-html-preview table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse; }
            .raw-html-preview td, .raw-html-preview th { word-break: break-word !important; white-space: normal !important; }
            .raw-html-preview pre { white-space: pre-wrap !important; word-break: break-word !important; max-width: 100% !important; overflow-x: auto; }
          `}</style>

          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <h3 className="font-semibold text-lg mb-0">
                  📋 Status & Notes
                </h3>
                <div className="text-xs text-gray-500">
                  Write notes (HTML supported).
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Paste HTML & duplicate Edit fields removed here per your previous request */}
              </div>
            </div>

            <div className="flex gap-2 mb-3">
              {["Approved", "Rejected"].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setActiveStatus(status);
                    setFollowUpDate("");
                    setSubStatus("");
                    setFollowUpRemark("");
                  }}
                  className={`px-2 py-1 rounded text-sm font-medium ${activeStatus === status ? "bg-indigo-600 text-white" : "bg-white border"}`}
                >
                  {status === "Approved" ? "Lead" : "Rejected"}
                </button>
              ))}
            </div>

            {activeStatus === "Approved" && (
              <>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium block mb-1">
                      Sub-Status
                    </label>
                    <select
                      value={subStatus}
                      onChange={(e) => {
                        const selected = e.target.value;
                        setSubStatus(selected);
                        setShowCalendar(selected === "Follow Up");
                        if (selected !== "Follow Up") {
                          setFollowUpDate("");
                          setFollowUpRemark("");
                        }
                      }}
                      className="w-full border px-2 py-1 rounded text-sm"
                    >
                      {subStatusOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>

                  {subStatus === "Follow Up" && (
                    <div className="flex-1">
                      <label className="text-sm font-medium block mb-1">
                        Remark
                      </label>
                      <select
                        value={followUpRemark}
                        onChange={(e) => setFollowUpRemark(e.target.value)}
                        className="w-full border px-2 py-1 rounded text-sm"
                      >
                        <option value="">Select remark...</option>
                        {followUpRemarkOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex-1">
                    <label className="text-sm font-medium block mb-1">
                      Label
                    </label>
                    <select
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      className="w-full border px-2 py-1 rounded text-sm"
                    >
                      <option value="">Select label…</option>
                      <option value="Hot">Hot</option>
                      <option value="Warm">Warm</option>
                      <option value="Cold">Cold</option>
                    </select>
                  </div>
                </div>

                {subStatus === "Follow Up" && showCalendar && (
                  <div className="mb-3">
                    <label className="text-sm block mb-1">
                      📅 Select Follow-up Date
                    </label>
                    <DatePicker
                      selected={followUpDate ? new Date(followUpDate) : null}
                      onChange={(date) => {
                        handleFollowUpPick(date);
                      }}
                      dateFormat="MM/dd/yyyy"
                      className="border px-2 py-1 rounded text-sm w-full"
                      placeholderText="Select a date"
                      inline
                    />
                  </div>
                )}
              </>
            )}

            <div className="text-sm text-gray-700 mb-2">
              Current Status:{" "}
              <span className="font-semibold">{activeStatus}</span>
            </div>

            {/* Editor or RAW preview */}
            <div className="mb-3 bg-white p-2 rounded">
              {appliedRawHtml ? (
                <div className="w-full">
                  <div className="mb-2 flex items-center gap-2">
                    <button
                      onClick={() => {
                        setAppliedRawHtml("");
                        if (editorRef.current) editorRef.current.innerHTML = "";
                      }}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      Clear Raw HTML
                    </button>
                    <button
                      onClick={() => {
                        if (!appliedRawHtml) {
                          toast.error("No raw HTML to open in editor.");
                          return;
                        }
                        if (editorRef.current)
                          editorRef.current.innerHTML = appliedRawHtml;
                        setAppliedRawHtml("");
                        toast.info("Opened raw HTML in editor for editing.");
                      }}
                      className="px-2 py-1 bg-indigo-600 text-white rounded text-sm"
                    >
                      Open in Editor
                    </button>

                    <div className="text-xs text-gray-500 ml-auto">
                      Preview (raw HTML preserved)
                    </div>
                  </div>

                  <div
                    className="raw-html-preview border rounded p-2"
                    style={{
                      minHeight: "200px",
                      maxWidth: "100%",
                      overflowX: "auto",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{ __html: appliedRawHtml }}
                  />
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="text-xs text-gray-500 ml-auto" />
                  </div>

                  <div
                    ref={editorRef}
                    contentEditable
                    onPaste={handleEditorPaste}
                    suppressContentEditableWarning
                    style={{
                      minHeight: 200,
                      border: "1px solid #e5e7eb",
                      padding: 8,
                      borderRadius: 6,
                      background: "#fff",
                      overflow: "auto",
                      maxWidth: "100%",
                    }}
                    className={`text-sm ${activeStatus === "Rejected" ? "pointer-events-none opacity-60" : ""}`}
                    aria-label="Rich editor"
                  />
                </>
              )}
            </div>

            {/* PDF upload input placed near the notes editor */}
            <div className="mb-3">
              <label className="text-sm font-medium block mb-1">
                📤 Attach PDF (optional)
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfUpload}
                disabled={uploadingPdf}
              />
              {uploadedPdfUrl && (
                <div className="mt-1 text-xs text-green-600">
                  ✅ Attached:{" "}
                  <a
                    href={
                      uploadedPdfUrl.startsWith("http")
                        ? uploadedPdfUrl
                        : `${BASE_URL}${uploadedPdfUrl}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    View PDF
                  </a>
                </div>
              )}
            </div>

            <div className="flex gap-2 mb-3 items-center">
              <button
                onClick={handleSaveStatus}
                disabled={
                  saveLoading ||
                  (activeStatus === "Approved" && (!label || label === "")) ||
                  (activeStatus === "Approved" &&
                    subStatus === "Follow Up" &&
                    (!followUpRemark || followUpRemark === ""))
                }
                className={`bg-gradient-to-r from-green-400 to-blue-500 text-white text-sm px-3 py-1 rounded font-medium self-start hover:from-green-500 hover:to-blue-600 ${saveLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                💾 {saveLoading ? "Saving..." : "Save Status"}
              </button>

              <button
                onClick={() => {
                  if (editorRef.current) editorRef.current.innerHTML = "";
                  setAppliedRawHtml("");
                  setCurrentNoteId(null);
                  setCurrentNoteTitle("");
                  setUploadedPdfUrl(null);
                }}
                className="text-sm px-3 py-1 border rounded"
              >
                Clear
              </button>

              <div className="text-xs text-gray-500 self-center">
                Notes save raw HTML (if pasted) or editor HTML. You can
                optionally attach a PDF.
              </div>
            </div>

            {/* notes list */}
            <div className="overflow-auto max-h-[40vh] max-w-[873px]">
              {(statusNotes[activeStatus] || []).map((note) => (
                <NoteItem
                  key={note.noteId || note.id || note.timestamp}
                  note={note}
                  status={activeStatus}
                  caseId={caseId}
                  onEdit={() => loadNoteIntoEditorForEdit(note)}
                  onDelete={() => handleDeleteNote(note)}
                  onUpdate={async (newText) => {
                    if (!newText.trim()) return alert("Note cannot be empty!");
                    try {
                      await axios.put(
                        `${BASE_URL}/api/case/${caseId}/edit-note`,
                        {
                          noteId: note.noteId || note.id,
                          caseId,
                          newText: newText.trim(),
                        },
                      );
                      const updatedList = [
                        ...(statusNotes[activeStatus] || []),
                      ];
                      const idx = updatedList.findIndex(
                        (n) => (n.noteId || n.id) === (note.noteId || note.id),
                      );
                      if (idx !== -1) {
                        updatedList[idx].text = newText.trim();
                        setStatusNotes({
                          ...statusNotes,
                          [activeStatus]: updatedList,
                        });
                      }
                      toast.success("Note updated.");
                    } catch (err) {
                      console.error("Error editing note:", err);
                      toast.error("Failed to edit note.");
                    }
                  }}
                  renderNoteContent={renderNoteContent}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Refresh popup */}
        {refreshPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-lg w-[300px]">
              <h2 className="text-sm font-semibold mb-4">
                🔄 Refresh Case Data
              </h2>
              <label className="text-sm block mb-1">📅 Select Date</label>
              <input
                type="date"
                value={refreshDate}
                onChange={(e) => setRefreshDate(e.target.value)}
                className="w-full border px-2 py-1 rounded text-sm mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setRefreshPopup(false)}
                  className="px-3 py-1 bg-gray-300 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!refreshDate)
                      return alert(
                        "⚠️ Please select a date before submitting.",
                      );
                    setRefreshLoading(true);
                    setRefreshPopup(false);
                    try {
                      let formattedDate = refreshDate;
                      if (refreshDate.includes("-")) {
                        const [y, m, d] = refreshDate.split("-");
                        formattedDate = `${m}/${d}/${y}`;
                      }
                      await axios.post(
                        `${SCRAPING_BASE_URL}/api/refreshCaseID`,
                        {
                          HARDCODED_DOCUMENT_CHECK_DATE_STR: formattedDate,
                          KINGS_COUNTY_TEXT: caseData.petition_court_type,
                          TARGET_FILE_NUMBERS: [caseId],
                        },
                      );
                      fetchCaseData();
                    } catch (err) {
                      console.error("Error refreshing case:", err);
                    } finally {
                      setRefreshLoading(false);
                    }
                  }}
                  disabled={refreshLoading}
                  className="px-3 py-1 bg-indigo-600 text-white rounded text-sm"
                >
                  {refreshLoading ? "Loading..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Paste HTML modal */}
        {showPasteHtml && (
          <div className="fixed inset-0 z-70 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl rounded p-4">
              <h3 className="font-semibold mb-2">Paste raw HTML here</h3>
              <textarea
                value={pasteHtmlText}
                onChange={(e) => setPasteHtmlText(e.target.value)}
                className="w-full h-64 border p-2 mb-3"
                placeholder="Paste full HTML block (including inline styles/classes)"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowPasteHtml(false);
                    setPasteHtmlText("");
                  }}
                  className="px-3 py-1 border rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={applyPastedHtml}
                  className="px-3 py-1 bg-indigo-600 text-white rounded"
                >
                  Apply HTML (preserve styles)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HTML Viewer Modal for Last Note */}
        {htmlModalOpen && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Last Note — Full HTML</h3>
                <div>
                  <button
                    onClick={() => setHtmlModalOpen(false)}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div
                className="raw-html-preview prose max-w-full text-sm"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlForView(htmlModalContent),
                }}
              />

              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={async () => {
                    try {
                      await copyToClipboard(
                        stripHtml(htmlModalContent),
                        "Copied plain text",
                        "Failed to copy plain text",
                      );
                    } catch (err) {
                      console.error("Copy plain text error:", err);
                      toast.error("Failed to copy plain text");
                    }
                  }}
                  className="px-3 py-1 border rounded text-sm"
                >
                  Copy plain text
                </button>

                <button
                  onClick={async () => {
                    try {
                      await copyToClipboard(
                        htmlModalContent,
                        "Copied raw HTML",
                        "Failed to copy raw HTML",
                      );
                    } catch (err) {
                      console.error("Copy raw HTML error:", err);
                      toast.error("Failed to copy raw HTML");
                    }
                  }}
                  className="px-3 py-1 border rounded text-sm"
                >
                  Copy raw HTML
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- Small components ---------- */

function MetaRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[12px] text-gray-500">{label}</div>
      <div className="text-sm text-gray-800 break-words">{value || "—"}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange = () => {},
  editable,
  textarea,
  className = "",
}) {
  return (
    <div className={`mb-3 ${className}`}>
      {label && (
        <label className="text-sm font-medium block mb-1">{label}</label>
      )}
      {editable ? (
        textarea ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border px-2 py-1 rounded text-sm h-20"
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border px-2 py-1 rounded text-sm"
          />
        )
      ) : (
        <div className="text-gray-700 text-sm break-words">{value || "—"}</div>
      )}
    </div>
  );
}

/* ---------- NoteItem (view-only PDF in list) ---------- */
function NoteItem({
  note,
  status,
  onUpdate,
  renderNoteContent,
  onEdit,
  onDelete,
  caseId,
}) {
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [editedText, setEditedText] = useState(note.text || "");

  useEffect(() => {
    setEditedText(note.text || "");
  }, [note.text]);

  const handleSaveInline = () => {
    onUpdate(editedText);
    setIsEditingInline(false);
  };

  // helper to build pdf href here too (prefer absolute else BASE_URL)
  const buildPdfHrefLocal = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://"))
      return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  const pdfHref =
    note.pdf_url || note.pdfUrl
      ? buildPdfHrefLocal(note.pdf_url || note.pdfUrl)
      : null;

  return (
    <div
      className={`text-sm mb-3 pl-2 border-l-4 ${
        status === "Approved" ? "border-green-500" : "border-red-500"
      }`}
    >
      {isEditingInline ? (
        <>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full text-sm border px-2 py-1 rounded"
          />
          <div className="flex gap-2 mt-1">
            <button
              onClick={handleSaveInline}
              className="text-green-600 text-xs font-semibold"
            >
              💾 Save
            </button>
            <button
              onClick={() => setIsEditingInline(false)}
              className="text-gray-500 text-xs"
            >
              ❌ Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Note text */}
          <div
            className="text-gray-800 mb-2"
            dangerouslySetInnerHTML={renderNoteContent(note.text)}
          />

          {/* Show PDF icon link only if available */}
          {pdfHref ? (
            <div className="mb-2">
              <a
                href={pdfHref}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                title="Open attached PDF"
              >
                <FiPaperclip size={14} /> View Attached PDF
              </a>
            </div>
          ) : null}

          {/* Footer */}
          <div className="text-xs text-gray-500 flex flex-col sm:flex-row sm:justify-between gap-2">
            <span className="block">{note.timestamp}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (onEdit) onEdit(note);
                }}
                className="text-indigo-500 text-xs inline-flex items-center gap-1"
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => {
                  if (onDelete) onDelete(note);
                }}
                className="text-red-500 text-xs"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
